import { useState } from 'react'
import { get } from 'idb-keyval'
import { useSnapshot } from 'valtio'
import { authState } from '../../hooks/useAuth'
import { apiService } from '../../api/axiosPrivateInstance'
import useToast from '../../hooks/useToast'
import getPersistableEntities from '../../model/cacheWhitelist'

const WHITELIST = getPersistableEntities()

function extractRecords(data) {
    if (!data) return null
    if (data.pages) return data.pages.flatMap(p => p.data || p)
    if (data.list) return data.list
    if (Array.isArray(data)) return data
    if (Array.isArray(data.data)) return data.data  // select/connect: { data: [...] }
    return null
}

function cacheToMockData(cache) {
    const result = {}
    const queries = cache?.clientState?.queries || []
    for (const query of queries) {
        const entityName = query.queryKey?.find(part => typeof part === 'string' && WHITELIST.includes(part))
        if (!entityName) continue
        const records = extractRecords(query.state?.data)
        if (!records?.length) continue
        if (!result[entityName]) result[entityName] = { data: [] }
        const existingIds = new Set(result[entityName].data.map(r => r.id))
        for (const record of records) {
            if (record.id && !existingIds.has(record.id)) {
                result[entityName].data.push(record)
                existingIds.add(record.id)
            }
        }
    }
    return result
}

const ExportCacheButton = () => {
    const [loading, setLoading] = useState(false)
    const [summary, setSummary] = useState(null)
    const { showToast, ToastComponent } = useToast()
    const orgId = useSnapshot(authState).data.orgId

    const handleExport = async () => {
        setLoading(true)
        setSummary(null)
        try {
            const cacheKey = `REACT_QUERY_OFFLINE_CACHE_${orgId}`
            const cache = await get(cacheKey)
            if (!cache) {
                showToast('No cache', 'No IndexedDB cache found — browse some data first', 'warning')
                return
            }
            const data = cacheToMockData(cache)
            const entityCount = Object.keys(data).length
            if (entityCount === 0) {
                showToast('Empty cache', 'No cacheable records found', 'warning')
                return
            }
            const response = await apiService.post('dev/export-cache', { data })
            setSummary({ entityCount: response.entityCount, recordCount: response.recordCount })
            showToast('Done', `Saved ${response.recordCount} records across ${response.entityCount} entities to mockData.json`, 'success')
        } catch (err) {
            showToast('Export failed', err?.message || 'Unknown error', 'error')
        } finally {
            setLoading(false)
        }
    }

    return (
        <div>
            {ToastComponent}
            <button
                onClick={handleExport}
                disabled={loading}
                style={{
                    padding: '8px 16px',
                    backgroundColor: loading ? '#6c757d' : '#495057',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                }}
            >
                {loading ? 'Exporting...' : 'Export Cache to Mock Data'}
            </button>
            {summary && (
                <span style={{ marginLeft: '1rem', fontSize: '0.85em', color: 'green' }}>
                    {summary.recordCount} records · {summary.entityCount} entities saved
                </span>
            )}
        </div>
    )
}

export default ExportCacheButton
